import { useEffect, useState } from 'react';
import { APP_CONTENT_PANE_ATTR } from '@hooks/useAppContentPane';

/** A heading counts as reached once its top is this close below the pane's top (headings scroll in to 80px). */
const REACHED_PX = 120;
/** Input that means the reader is moving the page themselves. */
const USER_SCROLL_INPUT = ['wheel', 'touchmove', 'keydown', 'pointerdown'] as const;
/** Of those, the ones that on a link are a click or Enter on it — the next pin, not a scroll. */
const LINK_INPUT: ReadonlySet<string> = new Set(['keydown', 'pointerdown']);

const isLinkInput = (event: Event): boolean =>
  LINK_INPUT.has(event.type) && event.target instanceof Element && event.target.closest('a') !== null;

/**
 * The heading the reader is in: the last one scrolled past the top of the
 * content pane, or the last heading of all once the pane is scrolled to its
 * end (the closing sections are too short ever to reach the top).
 *
 * `pinnedId` (the heading the URL points at) wins until the reader scrolls
 * by hand: a section picked from the table of contents stays lit even when
 * the page cannot scroll it to the top.
 *
 * `scope` (the guide slug) is part of the key: every guide numbers its
 * headings s0…sN, so two guides can share an id list.
 */
export function useScrollSpy(scope: string, ids: string[], pinnedId: string | null): string | null {
  const idList = ids.join('|');
  const key = `${scope}#${idList}`;
  // Tagged with the key it was measured under, so a new guide or id list starts unset.
  const [seen, setSeen] = useState<{ key: string; id: string | null } | null>(null);
  const [released, setReleased] = useState<string | null>(null);
  // A new pin starts held, even for a heading that was released before (adjusted during render, not in an effect).
  const [pinSeen, setPinSeen] = useState(pinnedId);
  if (pinSeen !== pinnedId) {
    setPinSeen(pinnedId);
    setReleased(null);
  }

  useEffect(() => {
    const headings = idList.split('|').map((id) => document.getElementById(id)).filter((element) => element !== null);
    if (headings.length === 0) return;
    const pane = headings[0]!.closest(`[${APP_CONTENT_PANE_ATTR}]`) ?? document.documentElement;
    const spy = () => {
      const scrollable = pane.scrollHeight > pane.clientHeight;
      const atEnd = scrollable && pane.scrollTop + pane.clientHeight >= pane.scrollHeight - 1;
      const line = Math.max(0, pane.getBoundingClientRect().top) + REACHED_PX;
      const reached = headings.filter((heading) => heading.getBoundingClientRect().top <= line);
      const id = atEnd ? headings.at(-1)!.id : (reached.at(-1)?.id ?? null);
      // Returning the same object bails out of the render: most scroll events change nothing.
      setSeen((previous) => (previous && previous.key === key && previous.id === id ? previous : { key, id }));
    };
    spy();
    // Capture: scroll does not bubble, and the pane (not the window) is what scrolls.
    document.addEventListener('scroll', spy, { capture: true, passive: true });
    return () => document.removeEventListener('scroll', spy, { capture: true });
  }, [key, idList]);

  useEffect(() => {
    if (pinnedId === null) return;
    const controller = new AbortController();
    // Releasing on a link press would light the scrolled-to section for the
    // instant before the click pins the new one: a visible flicker.
    const release = (event: Event) => {
      if (!isLinkInput(event)) setReleased(pinnedId);
    };
    for (const type of USER_SCROLL_INPUT) window.addEventListener(type, release, { passive: true, signal: controller.signal });
    return () => controller.abort();
  }, [pinnedId]);

  if (pinnedId !== null && pinnedId !== released && ids.includes(pinnedId)) return pinnedId;
  return seen && seen.key === key ? seen.id : null;
}
